import mqtt, {
  type IClientOptions,
  type IConnackPacket,
  type ISubscriptionGrant,
  type MqttClient,
  type Packet,
} from 'mqtt';
import Logger from '@gral/log';
import * as Sentry from '@sentry/react';
import envJson from '../../../.env.json';

//#region Config
const url: string = envJson.WSS_HOST || window.location.hostname;
const mqttUrl: string = `wss://${url}:8083`;

const log = Logger(`MQTT service`);
//#endregion Config

//#region Types (public)
export type MqttCredentials = {
  username: string;
  password: string;
};

export type GetClientCallbacks = {
  successCallback?: () => void;
  errorCallback?: (err: unknown) => void;
};

export type CloseConnectionOptions = {
  force?: boolean;
  /**
   * If true, clears desired subscriptions (topicListeners) as well.
   * If false, keeps desired subscriptions so a later connection can resume them.
   */
  clearSubscriptions?: boolean;
};
//#endregion Types (public)

//#region Connection derived state
// "transport connected" = socket/broker level
let _transportConnected = false;

// "app connected" = transportConnected AND there is at least 1 desired subscription
let _isConnected = false;

type IsConnectedListener = (isConnected: boolean) => void;
const _connListeners = new Set<IsConnectedListener>();

function _notifyIsConnected(): void {
  _connListeners.forEach((l) => {
    try {
      l(_isConnected);
    } catch (e) {
      log.error(e);
    }
  });
}

/**
 * IMPORTANT: _isConnected is derived.
 * Business rule: if there are no subscriptions, treat as disconnected.
 */
function _recomputeIsConnected(): void {
  const hasDesiredSubscriptions = topicListeners.size > 0;
  const next = Boolean(_transportConnected && hasDesiredSubscriptions);
  if (_isConnected === next) return;

  _isConnected = next;
  _notifyIsConnected();
}
//#endregion Connection derived state

//#region Connection public API
export function getIsConnected(): boolean {
  return _isConnected;
}

export function subscribeIsConnected(listener: IsConnectedListener): () => void {
  _connListeners.add(listener);
  listener(_isConnected); // emit current value immediately
  return () => _connListeners.delete(listener);
}
//#endregion Connection public API

//#region Subscriptions registry
// Payload can be anything; you parse JSON today.
type MessagePayload = unknown;
type TopicCallback = (incomingTopic: string, message: MessagePayload) => void;

type TopicEntry = {
  subTopics: Map<string | undefined, TopicCallback | undefined>;
  /**
   * This is an mqtt.js 'message' event listener bound to a specific broker topic entry.
   * We reattach these listeners to a recreated client when needed.
   */
  listener: (incomingTopic: string, message: Buffer) => void;
};

// Desired subscriptions. This is your "source of truth".
const topicListeners = new Map<string, TopicEntry>();
//#endregion Subscriptions registry

//#region Client lifecycle (state)
let _client: MqttClient | null = null;

/**
 * True only when we intentionally close the client (manual close).
 * Prevents auto-recreate from bringing it back up.
 */
let _manualClose = false;

// Credentials are cached so we can recreate the client on fatal stream errors.
let _lastCredentials: MqttCredentials | null = null;
let _lastCallbacks: GetClientCallbacks = {};

let _streamErrorAttempts = 0;
const MAX_STREAM_ERROR_RETRIES = 10;
let _recreateTimer: ReturnType<typeof setTimeout> | null = null;
//#endregion Client lifecycle (state)

//#region Client lifecycle (helpers)
function _cancelRecreateTimer(): void {
  if (_recreateTimer) {
    clearTimeout(_recreateTimer);
    _recreateTimer = null;
  }
}

/**
 * Attach all desired message listeners to the client.
 * We keep one mqtt.js 'message' listener per topic entry (as your legacy design).
 */
function _attachAllTopicMessageListeners(client: MqttClient): void {
  for (const entry of topicListeners.values()) {
    client.on('message', entry.listener);
  }
}

/**
 * Subscribe all desired broker topics (idempotent).
 * mqtt.js will re-subscribe after reconnect if clean/session config says so,
 * but doing it explicitly keeps behavior predictable (especially after recreation).
 */
function _subscribeAllTopics(client: MqttClient): void {
  for (const topic of topicListeners.keys()) {
    client.subscribe(topic, (err: Error | null, _granted?: ISubscriptionGrant[]) => {
      if (err) log.error('Resubscribe failed', topic, err);
      else log.info(`Subscribed to ${topic}`);
    });
  }
}

/**
 * Destroy current client quickly and recreate a new one with backoff,
 * up to MAX_STREAM_ERROR_RETRIES.
 *
 * Why: if you call client.end() on a stream error, mqtt.js reconnect loop stops.
 * Recreating the client is the simplest way to ensure retries also for "initial connect fails".
 */
function _scheduleRecreateClient(reason: unknown): void {
  if (_manualClose) return;
  if (!_lastCredentials) return;

  if (_streamErrorAttempts >= MAX_STREAM_ERROR_RETRIES) {
    log.info('STREAM ERROR RETRIES EXCEEDED');
    _transportConnected = false;
    _recomputeIsConnected();
    return;
  }

  _streamErrorAttempts += 1;

  // Basic backoff, capped
  const delayMs = Math.min(1000 * _streamErrorAttempts, 10_000);
  log.info('STREAM ERROR -> recreate client. Attempt:', _streamErrorAttempts, 'Delay(ms):', delayMs, reason);

  _cancelRecreateTimer();

  _recreateTimer = setTimeout(() => {
    if (_manualClose) return;

    try {
      _client?.end(true);
    } catch (e) {
      log.error(e);
    }

    _client = null;
    _transportConnected = false;
    _recomputeIsConnected();

    // Recreate using cached credentials/callbacks
    _createClient(_lastCredentials!, _lastCallbacks);
  }, delayMs);
}
//#endregion Client lifecycle (helpers)


function _createClient(
  { username, password }: MqttCredentials,
  { successCallback, errorCallback }: GetClientCallbacks = {}
): MqttClient {
  _lastCredentials = { username, password };
  _lastCallbacks = { successCallback, errorCallback };

  _manualClose = false;

  const options: IClientOptions = {
    username,
    password,
    reconnectPeriod: 10_000,
  };

  const client = mqtt.connect(mqttUrl, options);
  _client = client;

  // Attach all topic listeners immediately (so message routing works after connect)
  _attachAllTopicMessageListeners(client);

  // Stream errors are treated as fatal in many browser websocket scenarios.
  // We recreate the client to guarantee retry behavior.
  client.stream.on('error', (err: unknown) => {
    log.error(err);

    _transportConnected = false;
    _recomputeIsConnected();

    // Do not clear topicListeners here: we want to preserve desired subscriptions.
    // Recreate the client with backoff.
    _scheduleRecreateClient(err);

    // Pass the error up if needed
    errorCallback?.(err);
  });

  client.on('connect', (_packet: IConnackPacket) => {
    log.info('Connected');
    _transportConnected = true;

    // Reset fatal stream error retries once we get a successful connect
    _streamErrorAttempts = 0;

    // Ensure desired subscriptions are actually active on the broker
    _subscribeAllTopics(client);

    _recomputeIsConnected();
    successCallback?.();
  });

  client.on('reconnect', () => {
    // During reconnect attempt we consider transport not connected
    _transportConnected = false;
    _recomputeIsConnected();
  });

  client.on('offline', () => {
    _transportConnected = false;
    _recomputeIsConnected();
  });

  client.on('close', () => {
    log.info('Closed');

    _transportConnected = false;

    // IMPORTANT:
    // Do NOT clear topicListeners here if you want reconnect/recreate to continue streaming
    // with the same desired subscriptions.
    _recomputeIsConnected();

    // Also do NOT set _client = null here, because mqtt.js may reconnect internally.
    // _client is cleared only on manual close or when we intentionally recreate.
  });

  client.on('error', (error: unknown) => {
    // mqtt.js can emit 'error' while still trying reconnect. We do not kill the client here.
    log.info('MQTT ERROR', error);

    // If you want: report only after many attempts. We keep it conservative.
    const errorT = new Error('MQTTService');
    Sentry.captureException(errorT, {
      level: 'error',
      tags: { section: 'MQTTService' },
      extra: { error, mqttUrl, username },
    });

    errorCallback?.(error);
  });

  return client;
}


export function getClient(creds: MqttCredentials, callbacks: GetClientCallbacks = {}): MqttClient {
  // Cache credentials for auto-recreate behavior
  _lastCredentials = creds;
  _lastCallbacks = callbacks;

  if (_client) return _client;
  return _createClient(creds, callbacks);
}

//#region Public API - subscriptions
export function subscribe(
  client: MqttClient,
  topic: string,
  subTopic?: string,
  callback?: TopicCallback
): void {
  // If this is a new broker topic, create entry + listener and subscribe at broker level
  if (!topicListeners.has(topic)) {
    const listener: TopicEntry['listener'] = (incomingTopic, message) => {
      const decoded = new TextDecoder('utf-8').decode(message);
      const messageData: MessagePayload = JSON.parse(decoded);

      const subTopicListeners = topicListeners.get(topic)?.subTopics;
      if (!subTopicListeners) return;

      subTopicListeners.forEach((cb, subTopicKey) => {
        if (subTopicKey == null) return;
        if (incomingTopic.includes(subTopicKey) && cb) {
          cb(incomingTopic, messageData);
        }
      });
    };

    topicListeners.set(topic, { subTopics: new Map(), listener });

    // Attach the new listener to the current client
    client.on('message', listener);

    // Subscribe at broker level
    client.subscribe(topic, (err: Error | null) => {
      if (err) {
        log.error('Subscription request failed', topic, err);

        // Rollback
        try {
          client.removeListener('message', listener);
        } catch (e) {
          log.error(e);
        }

        _recomputeIsConnected();
        return;
      }

      log.info(`Subscribed to ${topic}`);
      _recomputeIsConnected();
    });
  }

  // Store callback for this subTopic
  const entry = topicListeners.get(topic);
  entry?.subTopics.set(subTopic, callback);

  // Derived state: having at least one topic means "connected" can become true once transport is up
  _recomputeIsConnected();
}

export function unsubscribe(client: MqttClient, topic: string, subTopic?: string): void {
  const topicEntry = topicListeners.get(topic);
  if (!topicEntry) return;

  const subTopicMap = topicEntry.subTopics;

  // Legacy-compat: if called as unsubscribe(client, topic) remove everything for that topic
  if (subTopic == null) subTopicMap.clear();
  else subTopicMap.delete(subTopic);

  // If no subtopics remain, unsubscribe broker topic + cleanup listener
  if (subTopicMap.size === 0) {
    client.unsubscribe(topic, (err?: Error | null) => {
      if (err) log.error('Unsubscription request failed', topic, err);
      else log.info(`Unsubscribed from ${topic}`);

      _recomputeIsConnected();
    });

    client.removeListener('message', topicEntry.listener);
    topicListeners.delete(topic);

    _recomputeIsConnected();
  }
}

export function onMessage<T = unknown>(
  client: MqttClient,
  subTopic: string,
  callBack: (topic: string, message: T) => void
): void {
  client.on('message', (topic: string, message: Buffer, _packet: Packet) => {
    if (!topic.includes(subTopic)) return;

    log.info(`New MQTT MSG from topic: ${topic}`);

    const decoded = new TextDecoder('utf-8').decode(message);
    const parsed = JSON.parse(decoded) as T;

    callBack(topic, parsed);
  });
}
//#endregion Public API - subscriptions

//#region Public API - teardown
/**
 * Manual close: stops auto-recreate and optionally clears desired subscriptions.
 * If clearSubscriptions=false, you can later call getClient() and it will resume subscriptions.
 */
export function closeConnection(
  client?: MqttClient | null,
  { force = true, clearSubscriptions = true }: CloseConnectionOptions = {}
): void {
  const c = client ?? _client;
  if (!c) return;

  log.info('Closing MQTT connection (manual)');

  _manualClose = true;
  _cancelRecreateTimer();

  if (clearSubscriptions) {
    try {
      for (const [topic, entry] of topicListeners.entries()) {
        c.removeListener('message', entry.listener);
        c.unsubscribe(topic);
      }
    } catch (e) {
      log.error(e);
    } finally {
      topicListeners.clear();
    }
  } else {
    // If we keep desired subscriptions, at least detach listeners from this client to avoid leaks.
    try {
      for (const entry of topicListeners.values()) {
        c.removeListener('message', entry.listener);
      }
    } catch (e) {
      log.error(e);
    }
  }

  _transportConnected = false;
  _recomputeIsConnected();

  try {
    c.end(force);
  } catch (e) {
    log.error(e);
  }

  if (c === _client) _client = null;
}
//#endregion Public API - teardown


const mqttService = {
  getClient,
  subscribe,
  onMessage,
  unsubscribe,
  closeConnection,
  getIsConnected,
  subscribeIsConnected,
};

export default mqttService;
